import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CardGlass from '../cards/card-glass.component';
import './tabbed-carousel.styles.scss';

// Constants moved outside component to prevent recreation
// Using design system colors from tailwind.config.js
const COLOR_VARIANTS = [
  '#7957d9', // Purple
  '#25F4F4', // Teal (aim-accent)
  '#295FB6', // Blue (aim-light-blue)
  '#7957d9', // Purple
  '#25F4F4', // Teal
  '#295FB6' // Blue
];

const COLOR_MAP = {
  '#7957d9': 'purple',
  '#25F4F4': 'teal',
  '#295FB6': 'light-blue',
  '#235dc2': 'blue'
};

const TabbedCarousel = ({ items = [], autoPlayInterval = 5000, className = '' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward

  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const containerRef = useRef(null);

  // Check if mobile - optimized with useCallback
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  // Get variant name from color - memoized
  const getVariantFromColor = useCallback((color) => {
    return COLOR_MAP[color] || 'purple';
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && !isDragging) {
      autoPlayRef.current = setInterval(() => {
        setActiveIndex((prevIndex) => {
          const nextIndex = prevIndex + direction;

          // If we're at the end and going forward, reverse direction
          if (nextIndex >= items.length) {
            setDirection(-1);
            return prevIndex - 1;
          }

          // If we're at the beginning and going backward, reverse direction
          if (nextIndex < 0) {
            setDirection(1);
            return prevIndex + 1;
          }

          return nextIndex;
        });
      }, autoPlayInterval);
    } else {
      clearInterval(autoPlayRef.current);
    }

    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, isDragging, autoPlayInterval, items.length, direction]);

  // Pause auto-play on interaction - memoized
  const handleInteraction = useCallback(() => {
    setIsAutoPlaying(false);
    clearInterval(autoPlayRef.current);
  }, []);

  // Resume auto-play after 3 seconds of no interaction
  useEffect(() => {
    if (!isAutoPlaying) {
      const resumeTimer = setTimeout(() => {
        setIsAutoPlaying(true);
      }, 3000);
      return () => clearTimeout(resumeTimer);
    }
  }, [isAutoPlaying]);

  // Navigation functions - memoized
  const goToSlide = useCallback(
    (index) => {
      setActiveIndex(index);
      setDirection(index > activeIndex ? 1 : -1);
      handleInteraction();
    },
    [activeIndex, handleInteraction]
  );

  const goToPrevious = useCallback(() => {
    if (activeIndex <= 0) return;
    setActiveIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      if (newIndex < 0) {
        setDirection(1);
        return 0;
      }
      return newIndex;
    });
    handleInteraction();
  }, [activeIndex, handleInteraction]);

  const goToNext = useCallback(() => {
    if (activeIndex >= items.length - 1) return;
    setActiveIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      if (newIndex >= items.length) {
        setDirection(-1);
        return items.length - 1;
      }
      return newIndex;
    });
    handleInteraction();
  }, [activeIndex, items.length, handleInteraction]);

  // Touch handlers for mobile - memoized
  const handleTouchStart = useCallback(
    (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      setCurrentX(e.touches[0].clientX);
      handleInteraction();
    },
    [handleInteraction]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setCurrentX(e.touches[0].clientX);
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    const deltaX = currentX - startX;
    const threshold = 50;

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        goToPrevious();
      } else {
        goToNext();
      }
    }

    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  }, [isDragging, currentX, startX, goToPrevious, goToNext]);

  // Mouse drag handlers for desktop - memoized
  const handleMouseDown = useCallback(
    (e) => {
      if (isMobile) return;
      setIsDragging(true);
      setStartX(e.clientX);
      setCurrentX(e.clientX);
      handleInteraction();
    },
    [isMobile, handleInteraction]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || isMobile) return;
      setCurrentX(e.clientX);
    },
    [isDragging, isMobile]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging || isMobile) return;

    const deltaX = currentX - startX;
    const threshold = 50;

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        goToPrevious();
      } else {
        goToNext();
      }
    }

    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  }, [isDragging, isMobile, currentX, startX, goToPrevious, goToNext]);

  // Calculate transform for slide animation - memoized
  const getSlideTransform = useMemo(() => {
    if (isDragging) {
      const deltaX = currentX - startX;
      const slideOffset = deltaX - activeIndex * (containerRef.current?.offsetWidth || 0);
      return `translateX(${slideOffset}px)`;
    }
    return `translateX(-${activeIndex * 100}%)`;
  }, [isDragging, currentX, startX, activeIndex]);

  // Memoize mobile cards to prevent unnecessary re-renders
  const mobileCards = useMemo(() => {
    return items.map((item, index) => {
      const color = COLOR_VARIANTS[index % COLOR_VARIANTS.length];
      const rightVariant = getVariantFromColor(color);

      return (
        <div key={item.id || index} className="tabbed-carousel__mobile-card">
          <h3 className="tabbed-carousel__mobile-title fs-h6 font-semibold">{item.label}</h3>
          <div className="tabbed-carousel__mobile-content">
            <CardGlass
              variant="default"
              titleStringsList={[item.leftTitle || item.title]}
              className="tabbed-carousel__mobile-half"
              customBorderColor="#6b7280"
              noGradient={false}
            >
              <p className="tabbed-carousel__paragraph fs-p">{item.leftContent || item.content}</p>
            </CardGlass>
            <CardGlass
              variant={rightVariant}
              titleStringsList={[item.rightTitle || item.title]}
              className="tabbed-carousel__mobile-half"
              noGradient={true}
            >
              <p className="tabbed-carousel__paragraph fs-p">{item.rightContent || item.content}</p>
            </CardGlass>
          </div>
        </div>
      );
    });
  }, [items, getVariantFromColor]);

  // Memoize tabs to prevent unnecessary re-renders
  const tabs = useMemo(() => {
    return items.map((item, index) => {
      const color = COLOR_VARIANTS[index % COLOR_VARIANTS.length];
      return (
        <button
          key={item.id || index}
          className={`tabbed-carousel__tab fs-p font-medium ${
            activeIndex === index ? 'tabbed-carousel__tab--active' : ''
          }`}
          onClick={() => goToSlide(index)}
          style={{
            '--tab-color': color
          }}
        >
          {item.label}
        </button>
      );
    });
  }, [items, activeIndex, goToSlide]);

  // Memoize desktop slides
  const desktopSlides = useMemo(() => {
    return items.map((item, index) => {
      const color = COLOR_VARIANTS[index % COLOR_VARIANTS.length];
      const rightVariant = getVariantFromColor(color);

      return (
        <div key={item.id || index} className="tabbed-carousel__slide">
          <div className="tabbed-carousel__slide-content">
            {/* Left Half - Always Grey */}
            <CardGlass
              variant="default"
              titleStringsList={[item.leftTitle || item.title]}
              className="tabbed-carousel__half tabbed-carousel__half--left"
              customBorderColor="#6b7280"
            >
              <p className="tabbed-carousel__paragraph fs-p">{item.leftContent || item.content}</p>
            </CardGlass>

            {/* Right Half - Tab Color */}
            <CardGlass
              variant={rightVariant}
              titleStringsList={[item.rightTitle || item.title]}
              className="tabbed-carousel__half tabbed-carousel__half--right"
              noGradient={true}
            >
              <p className="tabbed-carousel__paragraph fs-p">{item.rightContent || item.content}</p>
            </CardGlass>
          </div>
        </div>
      );
    });
  }, [items, getVariantFromColor]);

  // Memoize dots
  const dots = useMemo(() => {
    return items.map((_, index) => {
      const color = COLOR_VARIANTS[index % COLOR_VARIANTS.length];
      return (
        <button
          key={index}
          className={`tabbed-carousel__dot ${
            activeIndex === index ? 'tabbed-carousel__dot--active' : ''
          }`}
          onClick={() => goToSlide(index)}
          aria-label={`Go to slide ${index + 1}`}
          style={{
            '--dot-color': color
          }}
        />
      );
    });
  }, [items, activeIndex, goToSlide]);

  if (!items.length) return null;

  // Mobile layout - show all cards stacked
  if (isMobile) {
    return (
      <div className={`tabbed-carousel tabbed-carousel--mobile ${className}`}>
        <div className="tabbed-carousel__mobile-cards">{mobileCards}</div>
      </div>
    );
  }

  return (
    <div className={`tabbed-carousel ${className}`}>
      {/* Tabs */}
      <div className="tabbed-carousel__tabs">{tabs}</div>

      {/* Carousel Container */}
      <div
        className="tabbed-carousel__container"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          setIsDragging(false);
          setStartX(0);
          setCurrentX(0);
        }}
      >
        {/* Slides */}
        <div
          className="tabbed-carousel__slides"
          ref={containerRef}
          style={{
            transform: getSlideTransform,
            transition: isDragging ? 'none' : 'transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {desktopSlides}
        </div>

        {/* Navigation Arrows */}
        <button
          className="tabbed-carousel__arrow tabbed-carousel__arrow--left"
          onClick={goToPrevious}
          disabled={activeIndex <= 0}
          aria-label="Previous slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          className="tabbed-carousel__arrow tabbed-carousel__arrow--right"
          onClick={goToNext}
          disabled={activeIndex >= items.length - 1}
          aria-label="Next slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="tabbed-carousel__dots">{dots}</div>
    </div>
  );
};

export default TabbedCarousel;
